// Módulos de comunicación en 3D: Bluetooth HC-05/HC-06 y lector RFID RC522 con su tarjeta.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { BT, RFID, RFID_TAGS, RFID_ORDER, uidText } from '../../parts/comms.js';
import { getPins, PITCH } from '../../parts/registry.js';

const texCache = new Map();

function btSilk(type, pins) {
  if (texCache.has(type)) return texCache.get(type);
  const { c, g } = canvas(BT.w, BT.d, 22);
  const X = (x) => x + BT.w / 2;
  const Y = (z) => z + BT.d / 2;
  g.fillStyle = '#1b4fb3';
  g.fillRect(0, 0, BT.w, BT.d);
  for (const p of pins) text(g, p.name, X(p.x), Y(p.z) - 3.6, { size: 1.05, color: '#eef3ff', weight: 700, rotate: -Math.PI / 2, align: 'left' });
  text(g, type === 'bluetooth-hc05' ? 'ZS-040' : 'HC-06', X(0), Y(9), { size: 1.6, color: '#eef3ff', weight: 700 });
  text(g, 'LEVEL: 3.3V', X(0), Y(11.6), { size: 1.0, color: '#eef3ff', weight: 600 });
  text(g, 'POWER: 3.6V-6V', X(0), Y(13.4), { size: 1.0, color: '#eef3ff', weight: 600 });
  const t = texture(c);
  t.userData.shared = true;
  texCache.set(type, t);
  return t;
}

function btModuleSilk() {
  if (texCache.has('bt-mod')) return texCache.get('bt-mod');
  const { c, g } = canvas(13, 26, 22);
  g.fillStyle = '#1f6a37';
  g.fillRect(0, 0, 13, 26);
  // Antena impresa en zigzag
  g.strokeStyle = '#d6b25c';
  g.lineWidth = 0.55;
  g.beginPath();
  g.moveTo(1.5, 2);
  for (let i = 0; i < 5; i++) {
    g.lineTo(11.5, 2 + i * 1.6);
    g.lineTo(11.5, 2.8 + i * 1.6);
    g.lineTo(1.5, 2.8 + i * 1.6);
    g.lineTo(1.5, 3.6 + i * 1.6);
  }
  g.stroke();
  g.fillStyle = '#d6b25c';
  for (let i = 0; i < 13; i++) {
    g.fillRect(0, 11 + i * 1.15, 0.7, 0.6);
    g.fillRect(12.3, 11 + i * 1.15, 0.7, 0.6);
  }
  const t = texture(c);
  t.userData.shared = true;
  texCache.set('bt-mod', t);
  return t;
}

export function buildBluetooth(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const top = BT.pcbY + BT.t;
  b.root.add(extrudeBoard(boardShape(BT.w, BT.d, { r: 0.8 }), BT.t, btSilk(def.type, pins), '#163f8f', { w: BT.w, d: BT.d, y0: BT.pcbY }));
  // Módulo Bluetooth soldado encima (placa verde con antena impresa)
  const mod = new THREE.Mesh(new THREE.BoxGeometry(13, 1.0, 26), [mat('#1f6a37'), mat('#1f6a37'), new THREE.MeshStandardMaterial({ map: btModuleSilk(), roughness: 0.5 }), mat('#1f6a37'), mat('#1f6a37'), mat('#1f6a37')]);
  mod.position.set(0, top + 0.5, -4.5);
  mod.castShadow = true;
  b.root.add(mod);
  b.box(5.5, 0.9, 5.5, M.chip, 0, top + 1.0, 0);
  b.box(4, 0.7, 2.6, M.chip, 0, top + 1.0, -9);
  b.box(3.2, 1.2, 1.6, M.metal, -3.5, top + 1.0, 6);
  // Conector macho
  const zH = BT.d / 2 + 1.4;
  b.box(pins.length * PITCH, 2.5, PITCH, M.blackPlastic, 0, top, zH);
  for (const p of pins) b.box(0.64, 8.6, 0.64, M.gold, p.x, top, zH);
  b.box(BT.w - 2, 0.4, 3, mat('#1b4fb3'), 0, BT.pcbY, BT.d / 2 + 1);
  if (def.type === 'bluetooth-hc05') {
    b.box(3.6, 1.2, 3.6, M.metal, 4.6, top, 12.5);
    b.cyl(1.0, 1.0, 0.8, M.black, 4.6, top + 1.2, 12.5, 12);
  }
  b.build();
  const ledMat = emissiveMat('#7a1a14', '#ff2a1a');
  const led = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.9), ledMat);
  led.position.set(-4.6, top + 0.3, 12.5);
  b.root.add(led);
  const halo = glow('#ff2a1a', 9);
  halo.position.set(-4.6, top + 1.5, 12.5);
  b.root.add(halo);
  let mode = 'off';
  let t = 0;
  const setLed = (on) => {
    setEmissive(ledMat, on ? 1 : 0, 14);
    setGlow(halo, on ? 0.8 : 0);
  };
  return {
    root: b.root,
    update(v) {
      mode = v.mode || 'off';
    },
    animate(dt) {
      t += dt;
      // Parpadeo como el real: rápido esperando conexión, doble destello conectado, lento en modo AT.
      let on = false;
      if (mode === 'waiting') on = t % 0.4 < 0.2;
      else if (mode === 'paired') on = t % 2 < 0.1 || (t % 2 > 0.25 && t % 2 < 0.35);
      else if (mode === 'at') on = t % 2 < 1;
      setLed(on);
      return mode !== 'off';
    },
    reset() {
      mode = 'off';
      setLed(false);
    },
  };
}

function rfidSilk(pins) {
  if (texCache.has('rfid')) return texCache.get('rfid');
  const { c, g } = canvas(RFID.w, RFID.d, 16);
  const X = (x) => x + RFID.w / 2;
  const Y = (z) => z + RFID.d / 2;
  g.fillStyle = '#1953b4';
  g.fillRect(0, 0, RFID.w, RFID.d);
  // Antena: espiras rectangulares de cobre
  g.strokeStyle = '#c6a256';
  g.lineWidth = 0.7;
  for (let i = 0; i < 4; i++) {
    const m = 2.5 + i * 1.6;
    g.beginPath();
    g.roundRect(m, m, RFID.w - 2 * m, 36 - 2 * m, 2);
    g.stroke();
  }
  for (const p of pins) text(g, p.name, X(p.x), Y(p.z) - 3.4, { size: 1.15, color: '#eef3ff', weight: 700, rotate: -Math.PI / 2, align: 'left' });
  text(g, 'RFID-RC522', X(0), Y(13.5), { size: 2.0, color: '#eef3ff', weight: 800 });
  const t = texture(c);
  t.userData.shared = true;
  texCache.set('rfid', t);
  return t;
}

// Tarjetas y etiquetas ------------------------------------------------------------------------------------
/** Serigrafía de la tarjeta blanca (MIFARE 1K), del rótulo del llavero y de la cara de la etiqueta redonda y oscura (NTAG213). */
function tagTexture(id) {
  const key = 'tag-' + id;
  if (texCache.has(key)) return texCache.get(key);
  const uid = 'UID ' + uidText(RFID_TAGS[id].uid);
  let c;
  let g;
  if (id === 'card') {
    ({ c, g } = canvas(85.6, 54, 8));
    g.fillStyle = '#f6f6f2';
    g.fillRect(0, 0, 85.6, 54);
    g.strokeStyle = '#d8d8d2';
    g.lineWidth = 0.6;
    g.strokeRect(4, 4, 77.6, 46);
    text(g, 'MIFARE 1K', 42.8, 24, { size: 6, color: '#9aa0a8', weight: 700 });
    text(g, uid, 42.8, 34, { size: 4, color: '#b4b9bf', weight: 600 });
  } else if (id === 'fob') {
    ({ c, g } = canvas(18, 8, 24));
    g.fillStyle = '#f4f4f0';
    g.fillRect(0, 0, 18, 8);
    text(g, 'MIFARE 1K', 9, 2.8, { size: 2.1, color: '#6b7078', weight: 700 });
    text(g, uid, 9, 5.6, { size: 1.55, color: '#6b7078', weight: 600 });
  } else {
    ({ c, g } = canvas(26, 26, 20));
    g.fillStyle = '#2a2d33';
    g.fillRect(0, 0, 26, 26);
    // Antena impresa: espiras concéntricas y el chip al centro
    g.strokeStyle = '#5b616b';
    g.lineWidth = 0.35;
    for (let i = 0; i < 5; i++) {
      g.beginPath();
      g.arc(13, 13, 11.6 - i * 1.5, 0, Math.PI * 2);
      g.stroke();
    }
    g.fillStyle = '#aab0b8';
    g.fillRect(11.4, 11.4, 3.2, 3.2);
    text(g, 'NTAG213', 13, 6.6, { size: 2.6, color: '#d9dde2', weight: 700 });
    text(g, uid, 13, 20, { size: 1.15, color: '#b4bac2', weight: 600 });
  }
  const t = texture(c);
  t.userData.shared = true;
  texCache.set(key, t);
  return t;
}

/** Malla de cada tarjeta, centrada en su grosor y con el control para acercarla o alejarla. */
function tagMesh(id) {
  const tex = new THREE.MeshStandardMaterial({ map: tagTexture(id), roughness: 0.4 });
  let body;
  if (id === 'card') {
    body = new THREE.Mesh(new THREE.BoxGeometry(85.6, 0.8, 54), [mat('#f0f0ec'), mat('#f0f0ec'), tex, mat('#f0f0ec'), mat('#f0f0ec'), mat('#f0f0ec')]);
  } else if (id === 'fob') {
    const shape = new THREE.Shape();
    shape.moveTo(-10, -12);
    shape.quadraticCurveTo(0, -20, 10, -12);
    shape.lineTo(13, 10);
    shape.quadraticCurveTo(0, 20, -13, 10);
    shape.lineTo(-10, -12);
    const hole = new THREE.Path();
    hole.absarc(0, 11, 2.6, 0, Math.PI * 2, true);
    shape.holes.push(hole);
    const geo = new THREE.ExtrudeGeometry(shape, { depth: 4, bevelEnabled: true, bevelSize: 1, bevelThickness: 1, bevelSegments: 3 });
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, -2, 0);
    body = new THREE.Mesh(geo, mat('#2c63d8', { roughness: 0.35 }));
    // Rótulo blanco con el UID sobre la cara de arriba
    const label = new THREE.Mesh(new THREE.PlaneGeometry(18, 8).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: tagTexture(id), roughness: 0.5 }));
    label.position.set(0, 3.05, -1);
    body.add(label);
  } else {
    body = new THREE.Mesh(new THREE.CylinderGeometry(13, 13, 1.1, 48), [mat('#23262b'), tex, mat('#23262b')]);
  }
  body.castShadow = true;
  body.receiveShadow = true;
  const spec = RFID_TAGS[id];
  body.userData.interactive = { kind: 'toggle', key: spec.prop, label: `Acercar o alejar: ${spec.name}` };
  body.traverse((o) => {
    if (o !== body) o.userData.interactive = body.userData.interactive; // el rótulo del llavero también responde al clic
  });
  const group = new THREE.Group();
  group.add(body);
  return group;
}

// Dónde queda cada una sobre la mesa y, al acercarla, sobre la antena (se apilan según cuántas haya).
const TAG_AWAY = { card: { x: 68, y: 0.4, z: -6, ry: 0.08 }, tag: { x: -38, y: 0.55, z: -14, ry: 0 }, fob: { x: -38, y: 3, z: 22, ry: 0.5 } };
const TAG_SLOTS = [{ x: 0, z: -12, ry: 0 }, { x: -9, z: -17, ry: 0.35 }, { x: 11, z: -7, ry: -0.3 }];

export function buildRfid(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const top = RFID.pcbY + RFID.t;
  const holes = [[-17, -27, 1.5], [17, -27, 1.5], [-12.5, 22, 1.5], [12.5, 22, 1.5]];
  b.root.add(extrudeBoard(boardShape(RFID.w, RFID.d, { r: 1 }), RFID.t, rfidSilk(pins), '#123f8a', { holes, w: RFID.w, d: RFID.d, y0: RFID.pcbY }));
  b.box(5, 0.9, 5, M.chip, 0, top, 15, { ry: Math.PI / 4 });
  b.rbox(8, 2.2, 3.2, 0.9, M.metal, -10, top, 15);
  for (let i = 0; i < 10; i++) b.box(1.2, 0.5, 0.6, i % 2 ? M.chip : mat('#b8a07a', { roughness: 0.6 }), -14 + i * 3, top, 10 + (i % 3));
  const zH = RFID.d / 2 - 2.2;
  b.box(8 * PITCH, 2.5, PITCH, M.blackPlastic, 0, top, zH);
  for (const p of pins) b.box(0.64, 8.6, 0.64, M.gold, p.x, top, zH);
  b.build();
  const led = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.9), emissiveMat('#7a1a14', '#ff2a1a'));
  led.position.set(14, top + 0.3, 15);
  b.root.add(led);
  const halo = glow('#ff2a1a', 8);
  halo.position.set(14, top + 1.4, 15);
  b.root.add(halo);

  // Las tarjetas: las que están lejos descansan en la mesa; las cercanas flotan sobre la antena, una encima de otra.
  const near = {};
  const views = {};
  const place = (id) => {
    const v = views[id];
    v.group.position.set(v.cur.x, v.cur.y, v.cur.z);
    v.group.rotation.y = v.cur.ry;
  };
  const target = (id) => {
    if (!near[id]) return TAG_AWAY[id];
    const rank = RFID_ORDER.filter((x) => near[x]).indexOf(id);
    const slot = TAG_SLOTS[rank] || TAG_SLOTS[0];
    return { x: slot.x, y: top + 7 + rank * 7.5, z: slot.z, ry: slot.ry };
  };
  for (const id of RFID_ORDER) {
    near[id] = !!props[RFID_TAGS[id].prop];
    const group = tagMesh(id);
    b.root.add(group);
    views[id] = { group, cur: { ...target(id) } };
    place(id);
  }
  const setNear = (n) => {
    for (const id of RFID_ORDER) near[id] = !!n[id];
  };
  return {
    root: b.root,
    update(v) {
      if (v.near) setNear(v.near);
      setEmissive(led.material, v.on ? 0.7 : 0, 12);
      setGlow(halo, v.on ? 0.6 : 0);
    },
    setProps(p) {
      setNear(Object.fromEntries(RFID_ORDER.map((id) => [id, p[RFID_TAGS[id].prop]])));
    },
    animate(dt) {
      let moving = false;
      const k = 1 - Math.exp(-dt * 9);
      for (const id of RFID_ORDER) {
        const c = views[id].cur;
        const t = target(id);
        const d = Math.abs(t.x - c.x) + Math.abs(t.y - c.y) + Math.abs(t.z - c.z) + Math.abs(t.ry - c.ry) * 20;
        if (d < 0.02) {
          Object.assign(c, t);
        } else {
          for (const f of ['x', 'y', 'z', 'ry']) c[f] += (t[f] - c[f]) * k;
          moving = true;
        }
        place(id);
      }
      return moving;
    },
    reset() {
      setEmissive(led.material, 0, 12);
      setGlow(halo, 0);
    },
  };
}

export const MODELS = {
  'bluetooth-hc05': buildBluetooth,
  'bluetooth-hc06': buildBluetooth,
  'rfid-rc522': buildRfid,
};
